import {ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild} from "@angular/core";
import {LocalConfigurationService} from "@fedelta-pos/data-client";
import {SessionModel} from "../../orchestration/session.model";
import {ResponsiveService} from "../../orchestration/responsive.service";
import {LoadingStatus} from "../../common-types/loading-status";
import {Local} from "../../config-support/config";
import {CardDetails, CardScanService} from "../card-payment/card-scan.service";
import {LoadingService} from "../../ui-elements/loading/loading.service";
import {MessageLevel} from "../../notification/message/message";
import {GroupOrderModel} from "../../group-order/group-order.model";
import {IdentityModel} from "../../identity/identity.model";
import {OrderModel} from "../../order/order.model";
import {OrderingContextModel} from "../../ordering-context/ordering-context.model";
import {OrderEvents, OrderInitiationStatus, OrderSubmissionStatus} from "../../order/order-events";
import {FormComponent} from "../../inputs/form/form.component";
import {Subscription} from "rxjs";
import {OrderingPhase} from "../../orchestration/session-events";
import {Page} from "../../orchestration/page";
import {NavigationService} from "../../orchestration/navigation.service";
import {NavigationParams} from "../../orchestration/navigation-params";
import {RecentOrderData} from "../../order/recent-order.service";
import {CustomerDetailsService} from "../../customer-info/customer-details.service";
import {IntegratedPaymentProvider, PaymentCard, PaymentType} from "../payment";
import {OrderTransferTargetReferenceData} from "../../group-order/order-transfer-target-entity.model";
import {OrderType} from "../../order/order";
import {PaymentModel} from "../payment.model";

@Component({
    selector: "woa-payment-page",
    templateUrl: "./payment-page.component.html",
    providers: [{provide: Page, useExisting: PaymentPageComponent}]
})
export class PaymentPageComponent extends Page implements OnInit, OnDestroy {
    @ViewChild(FormComponent) public form: FormComponent;
    @ViewChild("deliveryDetails", {read: ElementRef}) private deliveryDetailsER: ElementRef;
    @ViewChild("customerDetails", {read: ElementRef}) private customerDetailsER: ElementRef;
    @ViewChild(PaymentCard, {read: ElementRef}) paymentCardER: ElementRef;
    @ViewChild(PaymentCard) paymentCard: PaymentCard;

    MessageLevel = MessageLevel;
    OrderSubmissionStatus = OrderSubmissionStatus;
    PaymentType = PaymentType;
    OrderEvents = OrderEvents;
    LoadingStatus = LoadingStatus;
    IntegratedPaymentProvider = IntegratedPaymentProvider;

    loadingStatus: LoadingStatus = undefined;
    hideFooterButtons: boolean = true;
    recentOrder: RecentOrderData;
    tipSelected: boolean;
    scannedCard: CardDetails;
    transferTarget: OrderTransferTargetReferenceData;

    private _paymentType: PaymentType;
    private subscriptions: Subscription = new Subscription();

    constructor(navigationParams: NavigationParams,
                private groupOrderModel: GroupOrderModel,
                private identityModel: IdentityModel,
                private navigationService: NavigationService,
                private changeDetectorRef: ChangeDetectorRef,
                private loadingService: LoadingService,
                private localConfigurationService: LocalConfigurationService,
                private cardScanService: CardScanService,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public responsiveService: ResponsiveService,
                public orderModel: OrderModel,
                public paymentModel: PaymentModel,
                private customerDetailsService: CustomerDetailsService) {
        super();

        if (this.sessionModel.waiterMode && this.sessionModel.devOrDemoMode) {
            this.scannedCard = {
                cardNumber: "4000000360000006",
                formattedCardNumber: "4000 0003 6000 0006",
                expiry: undefined // "12/34"
            };
        }
        if (!this.orderModel.paymentRequired) {
            this._paymentType = PaymentType.NONE;
        }

        // for display purposes only
        this.recentOrder = orderModel.buildRecentOrderData();
        this.recentOrder.orderPlacement.paymentAuthorisation.tipAmount = orderModel.tipAmount;

        this.tipSelected = this.orderingContextModel.tipSelected;
    }

    ngOnInit(): void {
        this.subscriptions.add(this.responsiveService.mode$.subscribe(() => {
            this.changeDetectorRef.detectChanges();
        }));
        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe((status: OrderInitiationStatus) => {
            if (status === OrderInitiationStatus.INITIATED) {
                this.recentOrder = this.orderModel.buildRecentOrderData();
                this.recentOrder.orderPlacement.paymentAuthorisation.tipAmount = this.orderModel.tipAmount;
                this.changeDetectorRef.detectChanges();
            }
        }));
        this.subscriptions.add(OrderEvents.orderSubmissionStatus$.subscribe(async (status: OrderSubmissionStatus) => {
            if (status === OrderSubmissionStatus.ORDER_INVALID) {
                return this.navigationService.dismissPage(this);
            }
            if (status === OrderSubmissionStatus.ORDER_CANCELLED) {
                return this.orderModel.completeOrder();
            }
            if (status === OrderSubmissionStatus.INCOMPLETE) {
                this.hideFooterButtons = false;
            }
            this.changeDetectorRef.detectChanges();
        }));

        // noinspection JSIgnoredPromiseFromCall
        this.load();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    // noinspection JSUnusedGlobalSymbols
    onActive(): void {
        this.sessionModel.orderingPhase = OrderingPhase.PAYMENT;
    }

    get paymentType(): PaymentType {
        return this._paymentType;
    }

    set paymentType(paymentType: PaymentType) {
        this._paymentType = paymentType;
        if (this.form) {
            this.form.markAsUnsubmitted();
        }
        if (this.paymentCard) {
            this.paymentCard.markAsUnsubmitted();
        }
        if (paymentType == PaymentType.NEW_CREDIT_CARD) {
            // noinspection JSIgnoredPromiseFromCall
            this.copyDefaultCardNr();
        }
    }

    get showFooter(): boolean {
        return this.loadingStatus === LoadingStatus.LOADED && !this.hideFooterButtons;
    }

    get firstName(): string {
        return this.orderingContextModel.firstName;
    }

    set firstName(firstName: string) {
        if (firstName !== this.orderingContextModel.firstName) {
            this.orderingContextModel.firstNameTrusted = true;
        }
        this.orderingContextModel.firstName = firstName;
    }

    get lastName(): string {
        return this.orderingContextModel.lastName;
    }

    set lastName(lastName: string) {
        if (lastName !== this.orderingContextModel.lastName) {
            this.orderingContextModel.lastNameTrusted = true;
        }
        this.orderingContextModel.lastName = lastName;
    }

    get email(): string {
        return this.orderingContextModel.email;
    }

    set email(email: string) {
        if (email !== this.orderingContextModel.email) {
            this.orderingContextModel.emailTrusted = true;
        }
        this.orderingContextModel.email = email ? email.toLowerCase() : email;
    }

    get mobilePhone(): string {
        return this.orderingContextModel.mobilePhone;
    }

    set mobilePhone(mobilePhone: string) {
        if (mobilePhone !== this.orderingContextModel.mobilePhone) {
            this.orderingContextModel.mobilePhoneTrusted = true;
        }
        this.orderingContextModel.mobilePhone = mobilePhone;
    }

    get walletPayment(): boolean {
        return this._paymentType === PaymentType.APPLE_PAY || this._paymentType === PaymentType.GOOGLE_PAY;
    }

    get paymentProviderProvidesCustomerDetails(): boolean {
        return this.walletPayment && this.paymentModel.paymentProvider !== IntegratedPaymentProvider.TYRO;
    }

    get showNameField(): boolean {
        return this.orderingContextModel.showName && ((this.identityModel.isGuestUser && !this.paymentProviderProvidesCustomerDetails) || this.sessionModel.waiterMode);
    }

    get showPhoneNumberField(): boolean {
        return this.orderingContextModel.showPhoneNumber && !this.paymentProviderProvidesCustomerDetails && this.paymentType !== PaymentType.GROUP_PAY;
    }

    get showEmailField(): boolean {
        return ((this.orderingContextModel.showEmail || this.orderingContextModel.showMarketingOptIn) &&
                (this.identityModel.isGuestUser || !this.identityModel.loginIdentityIsEmail) &&
                !this.paymentProviderProvidesCustomerDetails && this.paymentType !== PaymentType.GROUP_PAY) ||
            (this.sessionModel.waiterMode && this.sessionModel.canSendOrderReceivedEmail);
    }

    get showCustomerDetails(): boolean {
        return (this._paymentType !== undefined || this.sessionModel.waiterMode) && !this.paymentProviderProvidesCustomerDetails &&
            (this.showNameField || this.showEmailField || this.orderingContextModel.showMarketingOptIn ||
                (this.showPhoneNumberField && !this.orderingContextModel.isConfirmedDeliveryOrdering) ||
                (this.sessionModel.waiterMode && this.sessionModel.canSendOrderReceivedEmail));
    }

    get showCardDetails(): boolean {
        return this.orderModel.paymentRequired && (this.sessionModel.waiterMode || this._paymentType === PaymentType.NEW_CREDIT_CARD);
    }

    get emailLabel(): string {
        return this.sessionModel.canSendOrderReceivedEmail ? "woa.label.payment.taxInvoiceEmailAddress" : "woa.label.common.email";
    }

    get requireFirstName(): boolean {
        return (this.orderingContextModel.requireFirstName && !this.walletPayment) || this.paymentType === PaymentType.GROUP_PAY;
    }

    get requireLastName(): boolean {
        return this.orderingContextModel.requireLastName && !this.walletPayment && this.paymentType !== PaymentType.GROUP_PAY;
    }

    get requireEmail(): boolean {
        return this.orderingContextModel.requireEmail && !this.walletPayment;
    }

    get requirePhoneNumber(): boolean {
        return this.orderingContextModel.requirePhoneNumber && !this.walletPayment;
    }

    get showSavedCreditCardPayment(): boolean {
        return this.paymentModel.hasSavedCreditCard;
    }

    get showNewCreditCardPayment(): boolean {
        return true;
    }

    get showPointsPayment(): boolean {
        return !this.identityModel.isGuestUser &&
            this.sessionModel.currentApp.supportsRedemption && this.orderModel.orderTotal > 0 &&
            !!this.orderModel.initiatedOrder.pointsRedeemable;
    }

    get canPayWithPoints(): boolean {
        return this.orderModel.initiatedOrder.memberAvailableRedemptionPoints > 0;
    }

    get showTips(): boolean {
        return this.paymentModel.paymentProvider !== IntegratedPaymentProvider.TYRO &&
            this.orderingContextModel.currentStore && this.orderingContextModel.currentStore.allowTips &&
            !this.tipSelected && (this.sessionModel.waiterMode || this.paymentModel.canTip(this._paymentType));
    }

    async load(): Promise<void> {
        try {
            if ((this.orderingContextModel.orderType === OrderType.ORDER_DELIVERY && this.orderingContextModel.currentStore.integratedDelivery) ||
                this.orderingContextModel.currentStore.paymentProvider === IntegratedPaymentProvider.TYRO) {
                this.loadingStatus = LoadingStatus.LOADING;
                this.changeDetectorRef.detectChanges();
                await this.loadingService.performWithSpinner(async () => {
                    this.orderModel.requestOrderInitiation("payment - load", false);
                    await this.orderModel.untilOrderInitiationComplete();
                    await this.paymentModel.untilWalletPaymentProcessorCreationResolved();
                }, 30000);
            }
            this.loadingStatus = LoadingStatus.LOADED;
            this.changeDetectorRef.detectChanges();

            if (!this.sessionModel.waiterMode) {
                const nrPaymentOptions: number =
                    this.orderModel.initiatedOrder.availableTransferTargets.length +
                    (this.showSavedCreditCardPayment ? 1 : 0) +
                    (this.showNewCreditCardPayment ? 1 : 0) +
                    (this.showPointsPayment ? 1 : 0) +
                    (this.paymentModel.canMakeGooglePayPayment ? 1 : 0) +
                    (this.paymentModel.canMakeApplePayPayment ? 1 : 0);
                if (nrPaymentOptions === 1) {
                    this._paymentType = this.orderModel.initiatedOrder.availableTransferTargets.length ? PaymentType.GROUP_PAY
                        : this.showSavedCreditCardPayment ? PaymentType.SAVED_CREDIT_CARD
                            : this.showNewCreditCardPayment ? PaymentType.NEW_CREDIT_CARD
                                : this.showPointsPayment ? PaymentType.POINTS
                                    : this.paymentModel.canMakeGooglePayPayment ? PaymentType.GOOGLE_PAY
                                        : this.paymentModel.canMakeApplePayPayment ? PaymentType.APPLE_PAY
                                            : undefined;
                    if (this._paymentType === PaymentType.GROUP_PAY) {
                        this.transferTarget = this.orderModel.initiatedOrder.availableTransferTargets[0];
                    }
                }
            }

            await this.sessionModel.checkLiveMode();

            if (this.cardScanService.isAvailable()) {
                await this.scanCard();
            }

            // If there is no payment required, and no further details to enter or confirm, we can immediately place the order.
            // Pickups and deliveries allow an order time to be selected, and guest orders require customer details.
            // In practice, this means that free table orders by members where a phone number is not required are the only case where this applies.
            if (!this.identityModel.isGuestUser && !this.orderModel.paymentRequired && this.orderingContextModel.isConfirmedTableOrdering && !this.sessionModel.currentApp.requirePhoneNumberForTable) {
                // noinspection ES6MissingAwait
                this.orderModel.placeOrder(PaymentType.NONE, this.transferTarget);
            } else {
                this.hideFooterButtons = false;
            }
        } catch (error) {
            this.loadingStatus = LoadingStatus.FAILED;
            console.log("PaymentPage - load failed:");
            console.log(error);
        }
    }

    tipChanged(): void {
        this.recentOrder = this.orderModel.buildRecentOrderData();
        this.recentOrder.orderPlacement.paymentAuthorisation.tipAmount = this.orderModel.tipAmount;
    }

    selectTransferTarget(transferTarget: OrderTransferTargetReferenceData): void {
        this.paymentType = PaymentType.GROUP_PAY;
        this.transferTarget = transferTarget;
    }

    forgetCard(): void {
        this.customerDetailsService.removePaymentCustomer();
    }

    async copyDefaultCardNr(): Promise<void> {
        if (await this.localConfigurationService.getBoolean(Local.DEMO)) {
            try {
                const result: any = await navigator["permissions"].query({name: "clipboard-write"});
                if (result.state === "granted" || result.state === "prompt") {
                    // noinspection JSUnresolvedReference
                    navigator["clipboard"].writeText(this.paymentModel.paymentProvider === IntegratedPaymentProvider.STRIPE ? "4000000360000006"
                        : this.paymentModel.paymentProvider === IntegratedPaymentProvider.TYRO ? "4111111111111111"
                            : this.paymentModel.paymentProvider === IntegratedPaymentProvider.OOLIO ? "4002 6900 0000 0008"
                                : "11111111111111111111");
                }
            } catch (error) {
            }
        }
    }

    async scanCard(): Promise<void> {
        try {
            const card: CardDetails = await this.cardScanService.scanCard();
            if (card.cardNumber) {
                this.scannedCard = card;
            } else {
                this.scannedCard = undefined;
            }
        } catch (e) {
            this.scannedCard = undefined;
        }
    }

    async placeOrder(): Promise<any> {

        // Card validation errors may be detectable prior to an attempted payment authorisation (e.g. stripe),
        // which is handled by the validate() callback passed to placeOrder().
        // Alternatively (e.g. tyro), card validation (and any other authorisation) errors may be detected only once
        // authorisation is attempted. A local validation error in this case is notified via the cardValidationFailed() callback.
        return this.orderModel.placeOrder(this._paymentType, this.transferTarget, this.paymentCard,
            () => {
                if (this._paymentType === PaymentType.NEW_CREDIT_CARD && this.paymentCard && !this.paymentCard.isValid()) {
                    if (this.paymentCardER) {
                        setTimeout(() => this.paymentCardER.nativeElement.scrollIntoView({
                            block: "start",
                            inline: "nearest",
                            behavior: "smooth"
                        }), 330);
                    }
                    return false;
                }

                if (!this.form.valid) {
                    const fieldGroup: ElementRef = this.deliveryDetailsER || this.customerDetailsER;
                    if (fieldGroup) {
                        setTimeout(() => fieldGroup.nativeElement.scrollIntoView({
                            block: "start",
                            inline: "nearest",
                            behavior: "smooth"
                        }), 330);
                    }
                    return false;
                }

                return true;
            },
            () => {
                if (this._paymentType === PaymentType.NEW_CREDIT_CARD && this.paymentCardER) {
                    setTimeout(() => this.paymentCardER.nativeElement.scrollIntoView({
                        block: "start",
                        inline: "nearest",
                        behavior: "smooth"
                    }), 330);
                }
            });
    }
}



// WEBPACK FOOTER //
// ./src/app/payment/payment-page/payment-page.component.ts